import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setTrips, setLoading, setError } from '../../store/slices/tripSlice';
import { RootState } from '../../store/store';
import { Trip } from '../../types/trip';
import { api } from '../../services/api';
import { useTheme, useMediaQuery, Divider, Button, TextField, InputAdornment, Box, Tooltip, Avatar, IconButton, Fab } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import RefreshIcon from '@mui/icons-material/Refresh';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { getLegacyTripShortform } from '../../components/common/TripTypeIcon';
import { GridColDef } from '@mui/x-data-grid';
import {
  StyledPaper,
  PanelContainer,
  PanelTitle,
  ContentWrapper
} from './styles/LeftPanel.styles';
import {
  SearchField,
  PrimaryActionButton,
  SecondaryActionButton,
  CustomColorButton,
  LoadingContainer,
  ErrorContainer
} from './styles/Common.styles';
import { StyledDataGrid } from './styles/DataGrid.styles';
import ResizablePanel from './ResizablePanel';
import { TripFormPersistenceService } from '../../services/tripFormPersistence';
import TripCalendarModal from './midsections/calendarModel/TripCalendarModal';

// The centralized helper function is now imported from TripTypeIcon component

const columns: GridColDef[] = [
  {
    field: 'tripCode',
    headerName: 'Code',
    flex: 1,
    minWidth: 80,
    renderCell: (params: any) => {
      const trip = params.row;
      const tripCode = trip.tripCode || trip.trip_id || 'N/A';
      
      // Construct operator full name from firstName and lastName
      const firstName = trip.operator?.firstName || '';
      const lastName = trip.operator?.lastName || '';
      const operatorName = [firstName, lastName].filter(Boolean).join(' ') || 'Unknown';
      
      const tooltipText = `Trip Code: ${tripCode}\nCreated by: ${operatorName}`;

      return (
        <Tooltip title={tooltipText} arrow placement="top">
          <span
            style={{
              fontSize: '0.8rem',
              fontWeight: 'bold',
              color: 'rgba(255, 255, 255, 0.9)',
              cursor: 'pointer'
            }}
          >
            {tripCode}
          </span>
        </Tooltip>
      );
    }
  },
  {
    field: 'service',
    headerName: 'Service',
    flex: 1.2,
    minWidth: 100,
    renderCell: (params: any) => {
      const trip = params.row;
      const tripType = trip.travelServiceDetail?.travelService?.name || trip.tripType || '';
      const slot = trip.travelServiceDetail?.slot || '';
      const request = trip.request || '';
      const destination = trip.destinationCities || trip.destination || trip.sourceCity || '';

      // For Pune Local, append the slot information to the trip type
      let fullTripType = tripType;
      if (tripType === 'Pune Local' && slot) {
        // Extract hours from slot (e.g., "12_HOURS" -> "12")
        const hoursMatch = slot.match(/(\d+)_HOURS?/i);
        if (hoursMatch) {
          fullTripType = `${tripType} ${hoursMatch[1]} Hours`;
        }
      }

      // Get the trip shortform and tooltip using the existing helper function
      const { shortform, tooltip, icon } = getLegacyTripShortform(
        fullTripType,
        request,
        destination
      );

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {icon && (
            <Box sx={{ display: 'flex', alignItems: 'center', fontSize: '1rem' }}>
              {icon}
            </Box>
          )}
          <span
            style={{
              fontWeight: 'medium',
              fontSize: '0.8rem',
              color: '#ffffff'
            }}
            title={tooltip}
          >
            {shortform}
          </span>
        </Box>
      );
    }
  },
  {
    field: 'startDatetime',
    headerName: 'PickUp',
    flex: 1,
    minWidth: 100,
    renderCell: (params: any) => {
      const date = new Date(params.value);
      return (
        <span style={{ fontSize: '0.8rem' }}>
          {date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            hour: '2-digit',
              minute: '2-digit',
              hour12: false
            }).replace(',', '')}
        </span>
      );
    }
  },
  {
    field: 'customerMobile',
    headerName: 'Mobile',
    flex: 1,
    minWidth: 115,
    renderCell: (params: any) => {
      const customer = params.row.customer;
      const customerMobile = customer?.customerMobile || customer?.mobileNumber || 'N/A';

      return (
        <span style={{
          fontSize: '0.8rem',
          color: 'rgba(255, 255, 255, 0.9)',
          fontFamily: 'monospace'
        }}>
          {customerMobile}
        </span>
      );
    }
  },
  {
    field: 'customerName',
    headerName: 'Name',
    flex: 1,
    minWidth: 100,
    renderCell: (params: any) => {
      const customer = params.row.customer;
      const customerName = customer?.customerName || customer?.name || 'N/A';

      // Show tooltip if name is long
      const maxLen = 12;
      const showTooltip = customerName.length > maxLen;
      const displayName = showTooltip ? customerName.slice(0, maxLen) + '...' : customerName;

      return (
        <span
          style={{
            fontSize: '0.8rem',
            color: 'rgba(255, 255, 255, 0.9)',
            fontFamily: 'monospace',
            cursor: showTooltip ? 'pointer' : 'default'
          }}
          title={showTooltip ? customerName : undefined}
        >
          {displayName}
        </span>
      );
    }
  }
];

const gridProps = {
  autoHeight: false,
  headerHeight: 36,
  rowHeight: 45,
  hideFooter: true,
  disableRowSelectionOnClick: true,
  disableColumnMenu: true,
  disableExtendRowFullWidth: false,
  density: 'compact' as const
};

interface LeftPanelProps {
  onTripClick: (trip: any) => void; // Changed to any to handle new structure
}

const LeftPanel: React.FC<LeftPanelProps> = ({ onTripClick }) => {
  // Use Redux state instead of local state
  const dispatch = useDispatch();
  const { trips: allTrips, loading, error } = useSelector((state: RootState) => state.trips);
  const { user } = useSelector((state: RootState) => state.auth);
  const [categorizedTrips, setCategorizedTrips] = useState<any>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [calendarModalOpen, setCalendarModalOpen] = useState(false);

  // New state for calendar modal
  const [selectedTrip, setSelectedTrip] = useState<any>(null);

  const getUserRoleFromLocalStorage = () => {
    try {
      const authData = localStorage.getItem('auth');
      if (authData) {
        const parsed = JSON.parse(authData);
        return parsed.user?.role;
      }
    } catch (error) {
      console.error('Error parsing auth from localStorage:', error);
    }
    return null;
  };
const userRole = getUserRoleFromLocalStorage();
const isAccountant = userRole === 'ACCOUNTANT';
// Update the filterTrips function in LeftPanel.tsx
const filterTrips = (trips: any[]) => {
  let processedTrips = [...trips];

  // Apply search filter if query exists
  if (searchQuery.trim()) {
    const query = searchQuery.toLowerCase();
    processedTrips = processedTrips.filter(trip => {
      const tripCode = trip.tripCode;
      const tripType = trip.travelServiceDetail?.travelService?.name || trip.tripType || '';
      const customerMobile = trip.customer?.customerMobile || trip.customer?.mobileNumber || '';
      const customerName = trip.customer?.customerName || trip.customer?.name || '';
      const sourceCity = trip.sourceCity;
      const destinationCities = trip.destinationCities;

      // Get shortform for service search
      const { shortform } = getLegacyTripShortform(
        tripType,
        trip.request || '',
        destinationCities || sourceCity || ''
      );

      return tripCode?.toLowerCase().includes(query) ||
        tripType.toLowerCase().includes(query) ||
        shortform.toLowerCase().includes(query) ||
        customerMobile.toLowerCase().includes(query) ||
        customerName.toLowerCase().includes(query) ||
        sourceCity?.toLowerCase().includes(query) ||
        destinationCities?.toLowerCase().includes(query);
    });
  }

  // Sort by date - NEWEST FIRST (high date first)
  return processedTrips.sort((a, b) => {
    const dateA = new Date(a.startDatetime || 0);
    const dateB = new Date(b.startDatetime || 0);
    
    // Sort in descending order (newest dates first)
    // Nov 11 -> Nov 10 -> Nov 9 -> Oct 5
    return dateA.getTime() - dateB.getTime();
  });
};

  // New fetch function using getAllTrips with Redux
  const fetchTrips = async () => {
    try {
      dispatch(setLoading(true));
      dispatch(setError(null)); // Clear any previous errors

      const response = await api.getAllTrips() as { status: boolean; data: any };

      if (response.status && response.data) {
        if (Array.isArray(response.data)) {
          let responseData = (response.data as Trip[]);
          response.data = responseData.filter(trip => trip.travelServiceDetail?.travelService?.name !== "OTHERS");
          // Update Redux store with trips
          dispatch(setTrips(response.data as Trip[]));
          
          const categories: Record<string, any[]> = {};
          const uniqueStatuses = new Set<string>();
          response.data.forEach((trip: any) => {
            const status = trip.tripStatus;
            if (status) {
              const normalizedStatus = status.toUpperCase().trim();
              uniqueStatuses.add(normalizedStatus);

              if (!categories[normalizedStatus]) {
                categories[normalizedStatus] = [];
              }
            }
          });
          // If no statuses found, create a default category
          if (uniqueStatuses.size === 0) {
            categories['UNKNOWN'] = [];
          }
          // Second pass: categorize trips
          response.data.forEach((trip: any) => {
            const status = trip.tripStatus;
            const normalizedStatus = status ? status.toUpperCase().trim() : 'UNKNOWN';

            // Initialize category if it doesn't exist (safety check)
            if (!categories[normalizedStatus]) {
              categories[normalizedStatus] = [];
            }

            categories[normalizedStatus].push(trip);
          });

          setCategorizedTrips(categories);

        } else if (typeof response.data === 'object') {
          // If response.data is already categorized
          setCategorizedTrips(response.data);
          dispatch(setTrips(Object.values(response.data).flat() as Trip[]));
        }
      } else {
        dispatch(setTrips([]));
        setCategorizedTrips({});
      }
      dispatch(setError(null));
    } catch (err) {
      console.error('Error fetching trips:', err);
      dispatch(setTrips([]));
      setCategorizedTrips({});
      dispatch(setError('Failed to fetch trips'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  // Add callback to refresh trips when new trip is created
  const handleTripCreated = () => {
    fetchTrips();
  };

  // Effect to categorize trips whenever allTrips changes
  useEffect(() => {
    if (allTrips.length > 0) {
      const categories: Record<string, any[]> = {};
      const uniqueStatuses = new Set<string>();
      
      // First pass: collect unique statuses
      allTrips.forEach((trip: any) => {
        const status = trip.tripStatus;
        if (status) {
          const normalizedStatus = status.toUpperCase().trim();
          uniqueStatuses.add(normalizedStatus);

          if (!categories[normalizedStatus]) {
            categories[normalizedStatus] = [];
          }
        }
      });
      
      // If no statuses found, create a default category
      if (uniqueStatuses.size === 0) {
        categories['UNKNOWN'] = [];
      }
      
      // Second pass: categorize trips
      allTrips.forEach((trip: any) => {
        const status = trip.tripStatus;
        const normalizedStatus = status ? status.toUpperCase().trim() : 'UNKNOWN';

        // Initialize category if it doesn't exist (safety check)
        if (!categories[normalizedStatus]) {
          categories[normalizedStatus] = [];
        }

        categories[normalizedStatus].push(trip);
      });

      setCategorizedTrips(categories);
    }
  }, [allTrips]);

  useEffect(() => {
    fetchTrips();
    const setIntervalId = setInterval(() => {
      fetchTrips();
    }, 3 * 60 * 1000); // Refresh every 3 minutes

    // Auto-open pricing guidelines when component mounts
    const pricingObj = {
      tripId: 'pricing',
      tripType: 'PRICING_GUIDELINES',
      carType: '',
      carModel: '',
      operator: ''
    };

    const timer = setTimeout(() => {
      onTripClick(pricingObj);
    }, 100);

    return () => {
      clearInterval(setIntervalId);
      clearTimeout(timer)};
  }, []);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [panelWidth, setPanelWidth] = useState(450);

  // Updated render function to always show categories
  const renderTripPanel = (title: string, trips: any[], sectionKey: string) => {
    const filteredTrips = filterTrips(trips);
    return (
      <PanelContainer key={sectionKey}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
          <PanelTitle>{title} ({filteredTrips.length})</PanelTitle>
          <IconButton size="small" sx={{ color: 'white' }}>
          </IconButton>
        </Box>
        <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.12)' }} />
        {filteredTrips.length > 0 && (
          <Box sx={{
            width: '100%',
            height: Math.min(filteredTrips.length * 45 + 36, 300),
            minHeight: '81px',
            '& .MuiDataGrid-root': {
              border: 'none',
            }
          }}>
            <StyledDataGrid
              rows={filteredTrips}
              columns={columns}
              getRowId={(row) => row.id}
              onRowClick={(params) => {
                // Check if we have persisted new trip form data
                const hasFormData = TripFormPersistenceService.hasFormData();
                onTripClick({
                  tripId: params.row.tripCode,
                  tripCode: params.row.tripCode,
                  tripType: 'TRIP_DETAILS',
                  carType: '',
                  carModel: '',
                  operator: '',
                  ...params.row
                });
              }}
              {...gridProps}
            />
          </Box>
        )}
        {filteredTrips.length === 0 && (
          <Box sx={{ p: 2, textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)' }}>
            No trips found
          </Box>
        )}
      </PanelContainer>
    );
  };

  return (
    <ResizablePanel
      width={panelWidth}
      height={window.innerHeight}
      onWidthChange={setPanelWidth}
      minWidth={200}
      maxWidth={600}
    >
      <StyledPaper elevation={3} sx={{ width: panelWidth, overflowX: 'auto', overflowY: 'auto' }}>
        <ContentWrapper>
          <PanelContainer sx={{ display: 'flex', justifyContent: 'center', mb: 0, gap: 0.5, flexDirection: 'column' }}>
            {/* Main Action - New Trip */}
            <Box sx={{ mb: 1 }}>
              <PrimaryActionButton
                variant="contained"
                color="primary"
                fullWidth
                size="medium"
                startIcon={<AddIcon sx={{ fontSize: '1.1rem' }} />}
                onClick={() => {
                  // Clear any persisted form data when creating a new trip
                  TripFormPersistenceService.clearFormData();

                  const newTripObj = {
                    tripId: 'new',
                    tripType: 'NEW_TRIP',
                    carType: '',
                    carModel: '',
                    operator: '',
                    onTripCreated: (createdTripData?: any) => {
                      // Always refresh trips list when a trip is created - call fetchTrips first
                      fetchTrips();
                      
                      if (createdTripData && createdTripData.tripCode) {
                        // Additional refresh after small delay to ensure backend is updated
                        setTimeout(() => {
                          fetchTrips();
                        }, 1000);
                      }
                      
                      // Handle DIRECT NAVIGATION from success dialog - SIMPLEST APPROACH
                      if (createdTripData && createdTripData.directNavigation && createdTripData.tripCode) {
                        onTripClick({
                          tripId: createdTripData.tripCode,
                          trip_id: createdTripData.tripCode,
                          tripCode: createdTripData.tripCode,
                          tripType: 'TRIP_DETAILS',
                          carType: '',
                          carModel: '',
                          operator: '',
                          closeTabId: 'new' // Flag to close the 'new' tab
                        });
                        return;
                      }

                      // Handle other cases (tab updates without navigation)
                      if (createdTripData && createdTripData.tripCode && !createdTripData.navigateToDetails) {
                        onTripClick({
                          tripId: createdTripData.tripCode,
                          trip_id: createdTripData.tripCode,
                          tripCode: createdTripData.tripCode,
                          tripType: 'TRIP_DETAILS',
                          ...createdTripData
                        });
                      }
                    }
                  };
                  onTripClick(newTripObj);
                }}
                sx={{
                  py: 1.2,
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #464545 0%, #333333 100%)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #555555 0%, #464545 100%)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.4)',
                  },
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                Create New Trip
              </PrimaryActionButton>
            </Box>

            {/* Quick Actions Row */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: 1,
              mb: 1
            }}>
              <SecondaryActionButton
                variant="contained"
                color="secondary"
                size="small"
                startIcon={<InfoIcon sx={{ fontSize: '0.9rem' }} />}
                onClick={() => {
                  const pricingObj = {
                    tripId: 'pricing',
                    tripType: 'PRICING_GUIDELINES',
                  };
                  onTripClick(pricingObj);
                }}
                sx={{
                  py: 0.8,
                  px: 1,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  borderRadius: 1.5,
                  background: 'linear-gradient(135deg, #464545 0%, #333333 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #555555 0%, #464545 100%)',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.15s ease-in-out'
                }}
              >
                Pricing
              </SecondaryActionButton>        
              <CustomColorButton
                customColor="#464545"
                variant="contained"
                size="small"
                startIcon={<CalendarTodayIcon sx={{ fontSize: '0.9rem' }} />}
                onClick={() => setCalendarModalOpen(true)}
                sx={{
                  py: 0.8,
                  px: 1,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  borderRadius: 1.5,
                  background: 'linear-gradient(135deg, #464545 0%, #333333 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #555555 0%, #464545 100%)',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.15s ease-in-out'
                }}
              >
                Calendar
              </CustomColorButton>

               <CustomColorButton
                customColor="#464545"
                variant="contained"
                size="small"
                startIcon={<InfoIcon sx={{ fontSize: '0.9rem' }} />}
                onClick={() => {
                  const reportsObj = {
                    tripId: 'Reports',
                    tripType: 'TRIP_REPORTS',
                  };
                  onTripClick(reportsObj);
                }}
                sx={{
                  py: 0.8,
                  px: 1,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  borderRadius: 1.5,
                  background: 'linear-gradient(135deg, #464545 0%, #333333 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #555555 0%, #464545 100%)',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.15s ease-in-out'
                }}
              >
                Reports
              </CustomColorButton>

              <CustomColorButton
                customColor="#464545"
                variant="contained"
                size="small"
                onClick={() => {
                  const schedulerObj = {
                    tripId: 'Scheduler',
                    tripType: 'CAR_SCHEDULER',
                  };
                  onTripClick(schedulerObj);
                }}
                startIcon={<CalendarTodayIcon sx={{ fontSize: '0.9rem' }} />}
                sx={{
                  py: 0.8,
                  px: 1,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  borderRadius: 1.5,
                  background: 'linear-gradient(135deg, #464545 0%, #333333 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #555555 0%, #464545 100%)',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.15s ease-in-out'
                }}
              >
                Scheduler
              </CustomColorButton>
              <CustomColorButton
                customColor="#464545"
                variant="contained"
                size="small"
                onClick={() => {
                  const leadsReportObj = {
                    tripId: 'LeadsReport',
                    tripType: 'LEADS_REPORT',
                  };
                  onTripClick(leadsReportObj);
                }}
                startIcon={<InfoIcon sx={{ fontSize: '0.9rem' }} />}
                sx={{
                  py: 0.8,
                  px: 1,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  borderRadius: 1.5,
                  background: 'linear-gradient(135deg, #464545 0%, #333333 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #555555 0%, #464545 100%)',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.15s ease-in-out'
                }}
              >
                Leads Report
              </CustomColorButton>
            </Box>

            {/* Secondary Actions - Stacked Compact */}
            {/* <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.8,
              mb: 1.5
            }}>
              <CustomColorButton
                customColor="#464545"
                variant="contained"
                size="small"
                startIcon={<InfoIcon sx={{ fontSize: '0.85rem' }} />}
                onClick={() => {
                  const callbackObj = {
                    tripId: 'callback',
                    tripType: 'REQUEST_CALLBACK_PENDING',
                  };
                  onTripClick(callbackObj);
                }}
                sx={{
                  py: 0.7,
                  px: 1.5,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  borderRadius: 1.5,
                  background: 'linear-gradient(135deg, #464545 0%, #333333 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #555555 0%, #464545 100%)',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.15s ease-in-out'
                }}
              >
                Callback Requests
              </CustomColorButton>

              <CustomColorButton
                customColor="#464545"
                variant="contained"
                size="small"
                startIcon={<InfoIcon sx={{ fontSize: '0.85rem' }} />}
                onClick={() => {
                  const contactObj = {
                    tripId: 'contact',
                    tripType: 'CONTACT_REQUESTS',
                    carType: '',
                    carModel: '',
                    operator: ''
                  };
                  onTripClick(contactObj);
                }}
                sx={{
                  py: 0.7,
                  px: 1.5,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  borderRadius: 1.5,
                  background: 'linear-gradient(135deg, #464545 0%, #333333 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #555555 0%, #464545 100%)',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.15s ease-in-out'
                }}
              >
                Contact Requests
              </CustomColorButton>
            </Box> */}

            <SearchField
              fullWidth
              size="small"
              placeholder="Search trips..."
              value={searchQuery}
              // disabled={isAccountant}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '1rem' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchQuery('')}
                      sx={{
                        p: 0.5,
                        backgroundColor: 'rgba(255,255,255,0.15)',
                        borderRadius: '50%',
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,0.25)'
                        }
                      }}
                    >
                      <CloseIcon sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '1.2rem' }} />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  backdropFilter: 'blur(10px)',
                  // border: '1px solid rgba(255, 255, 255, 0.15)',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.12)',
                    // border: '1px solid rgba(255, 255, 255, 0.25)',
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    // border: '1px solid rgba(33, 150, 243, 0.6)',
                    boxShadow: '0 0 0 2px rgba(33, 150, 243, 0.15)',
                  }
                },
                '& .MuiInputBase-input': {
                  fontSize: '0.85rem',
                  py: 0.9
                }
              }}
            />
          </PanelContainer>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', m: 0, pr: 1 }}>
            <Button
              onClick={fetchTrips}
              // disabled={loading}
              size="small"
              variant="outlined"
              startIcon={
                <RefreshIcon sx={{
                  fontSize: '1rem',
                  animation: loading ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' },
                  }
                }} />
              }
              sx={{
                color: 'rgba(255, 255, 255, 0.8)',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                fontSize: '0.75rem',
                padding: '4px 8px',
                minWidth: 'auto',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
                '&:disabled': {
                  color: 'rgba(255, 255, 255, 0.3)',
                  borderColor: 'rgba(255, 255, 255, 0.1)',
                }
              }}
            >
              Refresh
            </Button>
          </Box>

          {error && (
            <ErrorContainer>
              <div style={{ color: theme.palette.error.main }}>{error}</div>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  dispatch(setError(null));
                  setLoading(true);
                  fetchTrips();
                }}
              >
                Retry
              </Button>
            </ErrorContainer>
          )}          
          

          {!error && (
            <>
                {isAccountant ? (
                  <>
                    {categorizedTrips['CLOSED'] && renderTripPanel(
                    'Closed Trips',
                    categorizedTrips['CLOSED'],
                    'CLOSED'
                  )}
                  {!categorizedTrips['CLOSED'] && (
                    <Box sx={{ p: 2, textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)' }}>
                      No closed trips found
                    </Box>
                  )}
                  </>
                ) : (
                  <>
              {/* OTHER section - only show when search has matching results */}
              {(() => {
                const mainCategories = [
                  'PENDING', 
                  'HOT LEAD',
                  'HOT_LEAD',
                  'HOT_LEADS',
                  'IN PROGRESS',
                  'CONFIRMED', 
                  'DRIVER ASSIGNED',
                  'IN CANCELLATION',
                ];

                // Collect all trips from other categories
                const otherTrips: any[] = [];
                Object.keys(categorizedTrips).forEach(category => {
                  if (!mainCategories.includes(category)) {
                    const trips = categorizedTrips[category] || [];
                    otherTrips.push(...trips);
                  }
                });

                // Only show OTHER section if search query exists AND has matching results
                if (!searchQuery.trim()) {
                  return null; // Hide when no search query
                }

                const filteredOtherTrips = filterTrips(otherTrips);
                if (filteredOtherTrips.length === 0) {
                  return null; // Hide when no matching results
                }

                // Show only when search finds matches in "other" trips
                return renderTripPanel(
                  'Other',
                  otherTrips,
                  'OTHER'
                );
              })()}


              {/* Dynamically render main priority categories */}
              {Object.keys(categorizedTrips).filter(category => {
                const mainCategories = [
                  'PENDING', 
                  'HOT LEAD',
                  'HOT_LEAD',
                  'HOT_LEADS',
                  'IN PROGRESS',
                  'CONFIRMED',
                  'COMPLETED', 
                  'DRIVER ASSIGNED',
                  'IN CANCELLATION',
                ];
                return mainCategories.includes(category);
              })
                .sort((a, b) => {
                  // If there's a search query, prioritize categories with matching results
                  if (searchQuery.trim()) {
                    const tripsA = categorizedTrips[a] || [];
                    const tripsB = categorizedTrips[b] || [];
                    const filteredTripsA = filterTrips(tripsA);
                    const filteredTripsB = filterTrips(tripsB);

                    // Categories with results come first
                    if (filteredTripsA.length > 0 && filteredTripsB.length === 0) return -1;
                    if (filteredTripsA.length === 0 && filteredTripsB.length > 0) return 1;

                    // Among categories with results, sort by number of results (descending)
                    if (filteredTripsA.length > 0 && filteredTripsB.length > 0) {
                      return filteredTripsB.length - filteredTripsA.length;
                    }
                  }

                  // Default sorting when no search or both categories have same result status
                  const statusOrder = ['PENDING','HOT LEAD','HOT_LEAD','HOT_LEADS', 'IN PROGRESS','DRIVER ASSIGNED', 'CONFIRMED',  'COMPLETED', 'IN CANCELLATION'];
                  const aIndex = statusOrder.indexOf(a);
                  const bIndex = statusOrder.indexOf(b);

                  // If both statuses are in the preferred order, sort by their index
                  if (aIndex !== -1 && bIndex !== -1) {
                    return aIndex - bIndex;
                  }

                  // If only one status is in the preferred order, prioritize it
                  if (aIndex !== -1) return -1;
                  if (bIndex !== -1) return 1;

                  // If neither status is in the preferred order, sort alphabetically
                  return a.localeCompare(b);
                })
                .map(category => {
                  const trips = categorizedTrips[category] || [];

                  // Format the title for each category
                  let title = '';
                  if (category === 'PENDING') title = 'Pending Enquiries';
                  else if (category === 'HOT_LEAD' || category === 'HOT_LEADS') title = 'Hot Leads';
                  else if (category === 'IN CANCELLATION') title = 'In Cancellation';
                  else title = category.charAt(0).toUpperCase() + category.slice(1).toLowerCase().replace(/_/g, ' ');

                  return renderTripPanel(
                    title,
                    trips,
                    category
                  );
                })}
                </>
                )}
            </>
          )}


        </ContentWrapper>
      </StyledPaper>

      {/* Trip Calendar Modal */}
      <TripCalendarModal
        open={calendarModalOpen}
        onClose={() => setCalendarModalOpen(false)}
        onTripClick={onTripClick}
      />
    </ResizablePanel>
  );
};

export default LeftPanel;

